import { WithID, WithRef } from "@converge-collective/common/models/Base";
import { Chat, ChatMessage } from "@converge-collective/common/models/Chat";
import { liteProfile } from "@converge-collective/common/models/Profile";
import { shallowTSToDates } from "@converge-collective/common/util";
import { Box, Stack, TextField } from "@mui/material";
import { isEmpty, take } from "lodash";
import React, { useEffect, useRef } from "react";
import { useUser } from "reactfire";
import useProfile from "~/hooks/useProfile";
import UserAvatar from "../user/UserAvatar";
import ChatMessageUI from "./ChatMessageUI";

const MaxRecentMessages = 50;
export default function ChatMessages({
  chat,
}: {
  chat: WithRef<WithID<Chat>>;
}): React.ReactElement {
  const { data: user } = useUser();
  const [profile] = useProfile(user?.uid);
  const [body, setBody] = React.useState("");
  // const FieldValue = useFirestore.FieldValue;

  // auto scroll to bottom on load
  const scrollContainerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    console.log("attempt to scroll");
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
      console.log("scroll to bottom", {
        scrollTop: scrollContainerRef.current.scrollHeight,
      });
    }
  }, []);

  const submit = async () => {
    if (profile) {
      setBody("");
      const newMesage: ChatMessage = {
        from: liteProfile(profile),
        body: body,
        date: new Date(),
        seenByUidsAt: {},
      };
      const recentMessages = take(
        [newMesage, ...chat.recentMessages],
        MaxRecentMessages
      );

      // we can't use arrayUnion because it won't add the same element twice
      // chat.ref.update({
      //   recentMessages: FieldValue.arrayUnion(newMesage),
      //   lastActivityAt: new Date(),
      // });

      // instead we recompute recentMessages - however, we can potentially lose
      // messages if the user sends them quickly. BUT: the "messages"
      // sub-collection will contain the full history because it's not rate
      // limited in any way. if it becomes a problem we could have some
      // reconciliation or rate limiting thing. e.g. when a user submits 10
      // messages in 1 second instead of trying to udpate `recentMessages` 10
      // times, we queue them up and then update `recentMessages` once.
      chat.ref.update({
        recentMessages,
        lastActivityAt: new Date(),
      });

      // also add it to the collection for long term queryability
      chat.ref.collection("messages").add(newMesage);
    } else {
      console.error("No profile found for user", user?.uid);
    }
  };

  return (
    <>
      {chat && (
        <Box sx={{ pt: 0, pl: 1.5, height: "100%" }}>
          <Box
            ref={scrollContainerRef}
            sx={{
              overflowY: "scroll",
              bgcolor: "transparent",
              height: "calc(100% - 145px)",
            }}
          >
            {take(chat.recentMessages, MaxRecentMessages)
              .reverse()
              .map(shallowTSToDates)
              .map((m, idx) => (
                <ChatMessageUI key={idx} chatMessage={m} />
              ))}
            <div style={{ overflowAnchor: "auto", height: 1 }} />
          </Box>
          <Stack sx={{ pr: 1, pt: 1, pb: 1 }} direction="row">
            {profile && (
              <Box pr={1.6}>
                <UserAvatar profile={profile} />
              </Box>
            )}
            <TextField
              value={body}
              fullWidth
              size="small"
              placeholder="Type a message..."
              onChange={(e) => setBody(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isEmpty(body)) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </Stack>
        </Box>
      )}
    </>
  );
}
